import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getOrders } from './services/firebaseService';
import Header from './Header';
import Footer from './Footer';
import AdminSidebar from './AdminSidebar';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

const PAGE_SIZE = 20;

const fieldLabelClass = 'text-xs uppercase tracking-wider text-gray-500 font-semibold';
const fieldSelectClass =
  'border border-gray-200 rounded p-2 bg-white text-primary focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors text-sm';

const ManageOrders = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);

  const orderStatuses = [
    'pending',
    'confirmed',
    'processing',
    'shipped',
    'delivered',
    'cancelled'
  ];

  const statusColors = {
    pending: 'bg-yellow-100 text-yellow-800',
    confirmed: 'bg-blue-100 text-blue-800',
    processing: 'bg-purple-100 text-purple-800',
    shipped: 'bg-indigo-100 text-indigo-800',
    delivered: 'bg-green-100 text-green-800',
    cancelled: 'bg-red-100 text-red-800'
  };

  const filteredOrders = orders.filter(
    (order) => statusFilter === 'all' || order.status === statusFilter
  );

  const totalPages = Math.max(1, Math.ceil(filteredOrders.length / PAGE_SIZE));
  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const endIndex = Math.min(startIndex + PAGE_SIZE, filteredOrders.length);
  const paginatedOrders = filteredOrders.slice(startIndex, endIndex);

  useEffect(() => {
    loadOrders();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const loadOrders = async () => {
    setLoading(true);
    const result = await getOrders();
    if (result.success) {
      setOrders(result.orders);
    } else {
      setError('Failed to load orders: ' + result.error);
    }
    setLoading(false);
  };

  const handleViewDetails = (order) => {
    navigate(`/order/${order.id}`);
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';

    let dateObj;
    if (date.toDate && typeof date.toDate === 'function') {
      dateObj = date.toDate();
    } else {
      dateObj = new Date(date);
    }

    return dateObj.toLocaleDateString('en-MY', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    const value = Number(amount);
    if (Number.isNaN(value)) return '—';
    return new Intl.NumberFormat('en-MY', {
      style: 'currency',
      currency: 'MYR'
    }).format(value);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />
      <div className="pt-20 flex flex-1">
        <AdminSidebar activeItem="orders" />
        <div className="flex-1 min-w-0 overflow-y-auto bg-gray-50 p-4 md:p-6">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-6 gap-4 border-b-2 border-gray-200 pb-4">
            <div>
              <button
                onClick={() => navigate('/admin-portal')}
                className="flex items-center gap-1 text-gray-500 hover:text-primary transition-colors mb-2 text-sm"
              >
                <ArrowBackIcon className="!w-[18px] !h-[18px]" />
                Back to Dashboard
              </button>
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900">All Orders</h1>
            </div>
            <div className="flex flex-col gap-1 self-start md:self-auto">
              <label className={fieldLabelClass} htmlFor="status_filter">
                Filter by Status
              </label>
              <select
                id="status_filter"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className={fieldSelectClass}
              >
                <option value="all">All Orders</option>
                {orderStatuses.map((status) => (
                  <option key={status} value={status}>
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}

          <div className="bg-white border border-gray-200 rounded shadow-sm overflow-x-auto">
            {loading && (
              <div className="p-8 text-center">
                <div className="text-gray-500">Loading orders...</div>
              </div>
            )}
            {!loading && filteredOrders.length === 0 && (
              <div className="p-8 text-center">
                <div className="text-gray-500">No orders found.</div>
              </div>
            )}
            {!loading && filteredOrders.length > 0 && (
              <>
                <table className="w-full text-left border-collapse min-w-[960px]">
                  <thead>
                    <tr className="bg-gray-100 border-b-2 border-gray-200">
                      <th className="py-2 px-4 text-xs uppercase tracking-wider font-semibold text-gray-900">Order ID</th>
                      <th className="py-2 px-4 text-xs uppercase tracking-wider font-semibold text-gray-900">Customer</th>
                      <th className="py-2 px-4 text-xs uppercase tracking-wider font-semibold text-gray-900">Items</th>
                      <th className="py-2 px-4 text-xs uppercase tracking-wider font-semibold text-gray-900 text-right">Total</th>
                      <th className="py-2 px-4 text-xs uppercase tracking-wider font-semibold text-gray-900">Status</th>
                      <th className="py-2 px-4 text-xs uppercase tracking-wider font-semibold text-gray-900">Date</th>
                    </tr>
                  </thead>
                  <tbody className="text-sm text-gray-900">
                    {paginatedOrders.map((order, index) => (
                      <tr
                        key={order.id}
                        className={`border-b border-gray-200 transition-colors hover:bg-gray-100 cursor-pointer ${
                          index % 2 === 1 ? 'bg-gray-50 hover:bg-gray-100' : ''
                        }`}
                        onClick={() => handleViewDetails(order)}
                      >
                        <td className="py-2 px-4 font-mono font-bold text-primary">
                          {order.orderId || order.id?.slice(-8).toUpperCase()}
                        </td>
                        <td className="py-2 px-4">
                          <div className="font-bold">{order.purchaserName}</div>
                          <div className="text-gray-500 text-xs">{order.purchaserPhoneNumber}</div>
                        </td>
                        <td className="py-2 px-4 max-w-xs">
                          {order.products?.slice(0, 2).map((item, idx) => (
                            <div key={idx} className="truncate">
                              {item.productName} x{item.quantity}
                            </div>
                          ))}
                          {order.products?.length > 2 && (
                            <div className="text-gray-500 text-xs">+{order.products.length - 2} more</div>
                          )}
                        </td>
                        <td className="py-2 px-4 text-right font-bold">
                          {formatCurrency(order.totalAmount || order.total)}
                        </td>
                        <td className="py-2 px-4">
                          <span
                            className={`inline-flex items-center px-2 py-1 text-[10px] uppercase tracking-wider font-semibold rounded whitespace-nowrap ${
                              statusColors[order.status] || 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {order.status?.charAt(0).toUpperCase() + order.status?.slice(1)}
                          </span>
                        </td>
                        <td className="py-2 px-4 text-gray-500">{formatDate(order.dateTime || order.createdAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div className="bg-white border-t border-gray-200 p-2 flex items-center justify-between">
                  <span className="text-sm text-gray-500 pl-2">
                    Showing {startIndex + 1} to {endIndex} of {filteredOrders.length} entries
                  </span>
                  <div className="flex items-center gap-1 pr-2">
                    <button
                      className="p-1 text-gray-400 hover:text-primary disabled:opacity-50"
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage((page) => page - 1)}
                      type="button"
                      aria-label="Previous page"
                    >
                      <ChevronLeftIcon className="!w-5 !h-5" />
                    </button>
                    {Array.from({ length: totalPages }, (_, index) => index + 1).map((page) => (
                      <button
                        key={page}
                        className={`w-8 h-8 rounded text-sm font-semibold flex items-center justify-center ${
                          page === currentPage
                            ? 'bg-primary text-white'
                            : 'text-gray-900 hover:bg-gray-100'
                        }`}
                        onClick={() => setCurrentPage(page)}
                        type="button"
                      >
                        {page}
                      </button>
                    ))}
                    <button
                      className="p-1 text-gray-400 hover:text-primary disabled:opacity-50"
                      disabled={currentPage === totalPages}
                      onClick={() => setCurrentPage((page) => page + 1)}
                      type="button"
                      aria-label="Next page"
                    >
                      <ChevronRightIcon className="!w-5 !h-5" />
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ManageOrders;
